import { yupResolver } from '@hookform/resolvers/yup'
import type {
  LocationSchema,
  WeeklyScheduleSchema
} from '@libs/data-access/entities'
import type {
  AccountSchema,
  ScheduleMembershipSchema,
  ShiftRQSchema
} from '@libs/data-access/queries'
import {
  planningEngineService,
  shiftService,
  type ApiErrorResponseData
} from '@libs/data-access/queries'
import { notifications } from '@libs/ui/ds'
import { useCommonNotificationsTranslation } from '@libs/utils/translations'
import { useMemoSelector } from '@spa/redux/hooks'
import { controllersActions } from '@spa/scenes/Plannings/redux/modules/controllers'
import { deleteEntityByIds } from '@spa/scenes/Plannings/redux/reducers'
import { getParams } from '@spa/scenes/Plannings/redux/selectors'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import type FormData from '../Models/FormData'
import { useShiftSchemaValidations } from '../ShiftFormLegacy.validations'
import createShiftArrayPayload from '../utils/createShiftArrayPayload'

import useShiftDefaultValues from './useShiftDefaultValues'

type ShiftForm = {
  currentAccount: AccountSchema
  currentMembership?: ScheduleMembershipSchema
  shift: ShiftRQSchema
  teamId: number
  labelId?: number
  weeklyScheduleIsPublished: boolean
  selectedTeamLocation?: LocationSchema
  weeklyscheduleLocation: LocationSchema
  weeklySchedules: WeeklyScheduleSchema[]
  onFormSuccess: () => void
}

const useShiftForm = ({
  currentAccount,
  currentMembership,
  shift,
  teamId,
  labelId,
  weeklyScheduleIsPublished,
  weeklyscheduleLocation,
  weeklySchedules,
  onFormSuccess
}: ShiftForm) => {
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<ApiErrorResponseData | null>(null)
  const dispatch = useDispatch()
  const params = useMemoSelector(getParams)

  const { t: tNotifications } = useCommonNotificationsTranslation()
  const shiftSchemaValidations = useShiftSchemaValidations()
  const isCreation = !shift?.id

  const { mutateAsync: createShift } = shiftService.api.useCreateShift()
  const { mutateAsync: updateShift } = shiftService.api.useUpdateShift()
  const { mutateAsync: deleteShift } = shiftService.api.useDeleteShift()

  const refreshPlanning = (refetchShifts = true) => {
    // Unvalidate the month view in RQ
    planningEngineService.row.invalidateRows()
    if (refetchShifts) {
      shiftService.api.invalidateShift()
    }
    // Refresh redux because... week view and others view are in redux
    dispatch(controllersActions.planningSilentRefresh(params))
  }

  const defaultValues = useShiftDefaultValues({
    account: currentAccount,
    membership: currentMembership,
    shift,
    weeklyscheduleLocation,
    teamId,
    labelId,
    weeklyScheduleIsPublished
  })

  const useFormMethods = useForm({
    mode: isCreation ? 'onSubmit' : 'onBlur',
    defaultValues,
    resolver: yupResolver(shiftSchemaValidations)
  })

  const onSubmit = (formData: FormData) => {
    const newWeeklyschedule = weeklySchedules.find(
      weeklyschedule => weeklyschedule.team_id === teamId
    )

    const payload = createShiftArrayPayload({
      formData,
      shift,
      weeklyscheduleLocation,
      account: currentAccount,
      newWeeklyschedule
    })

    setFormLoading(true)
    if (!isCreation) {
      return submitShiftUpdate(payload)
    }
    const isBulk = !(payload.length === 1 && payload[0]?.length === 1)
    if (isBulk) {
      submitBulkShiftCreation(payload)
    } else {
      submitShiftCreation(payload)
    }
  }

  const submitShiftUpdate = payload => {
    updateShift({
      pathParams: { shiftId: shift.id },
      payload: payload[0][0]
    })
      .then(() => refreshPlanning())
      .then(onFormSuccess)
      .catch((error: ApiErrorResponseData) => {
        setFormLoading(false)
        setFormError(error)
      })
  }

  const submitShiftCreation = payload => {
    createShift({ payload: payload[0][0] })
      .then(() => refreshPlanning())
      .then(onFormSuccess)
      .catch((error: ApiErrorResponseData) => {
        setFormLoading(false)
        setFormError(error)
      })
  }

  const submitBulkShiftCreation = payload => {
    const shiftPromises = payload.map(shiftBulk =>
      shiftService.bulk.api.bulkCreateShiftsList({
        payload: { shifts: shiftBulk }
      })
    )

    const promise = Promise.all(shiftPromises)

    notifications.promise(promise, {
      loading: { message: tNotifications('shift.bulk.loading') },
      success: { message: tNotifications('shift.bulk.success') },
      error: { message: tNotifications('shift.bulk.error') }
    })

    promise
      .then(() => refreshPlanning())
      .then(onFormSuccess)
      .catch(() => {
        setFormLoading(false)
      })
  }

  const onDeleteSubmit = ({ notify_user }) => {
    deleteShift({
      pathParams: { shiftId: shift.id },
      queryParams: { notify_user }
    })
      .then(() => {
        // Trick to remove unassigned shifts from redux when you delete it. On update/create, no need, but on deletion, you need that
        if (shift?.id) {
          const payload = {
            entity: 'shifts' as const,
            ids: [shift.id]
          }
          dispatch(deleteEntityByIds(payload))
        }
      })
      .then(() => refreshPlanning(false))
      .then(onFormSuccess)
      .catch(error => {
        setFormLoading(false)
        setFormError(error)
      })

    setFormLoading(true)
  }

  return {
    onDeleteSubmit,
    onSubmit,
    formLoading,
    formError,
    ...useFormMethods
  }
}

export default useShiftForm
